import { useState } from 'react';
import { api } from '../api';
import type { User } from '../api';
import { Banner, EmptyState, Spinner, useAsync } from '../components/Common';
import { formatDateTime } from '../format';
import { SyncAll } from '../components/SyncAll';
import { ShareSummary } from '../components/ShareSummary';

/**
 * The landing page, and for a director the ONLY page with numbers on it that
 * they did not ask for.
 *
 * Two rules shape everything here.
 *
 * 1. A missing figure is never drawn as zero. The server returns null for
 *    anything it has not measured, and null renders as an em dash with the
 *    reason underneath. "0 calls" and "Dialpad has never synced" look identical
 *    on a tile and mean opposite things, and only one of them is worth a
 *    conversation with a consultant.
 *
 * 2. Teams lead, people are one click in. A front page that ranks named
 *    individuals sets the tone for how the whole tool gets used, and several of
 *    these columns are dominated by bulk actions rather than effort.
 */

interface Coverage {
  slug: string;
  enabled: boolean;
  rows: number;
  lastSync: string | null;
  lastStatus: string | null;
  earliest: string | null;
  latest: string | null;
}

interface Row {
  [column: string]: unknown;
}

interface DashboardData {
  week: string | null;
  previousWeek: string | null;
  inProgressWeek: string | null;
  availableWeeks: string[];
  warnings: string[];
  weeksHeld: number;
  totals: Record<string, number | null>;
  previousTotals: Record<string, number | null>;
  openVacancies: number | null;
  agingUnworked: number | null;
  teams: Row[];
  people: Row[];
  trend: Row[];
  coverage: Coverage[];
}

const DATASET_LABELS: Record<string, string> = {
  'bullhorn-weekly-activity': 'Bullhorn weekly activity',
  'bullhorn-open-vacancies': 'Open vacancies',
  'bullhorn-placements': 'Placements',
  'dialpad-calls-daily': 'Dialpad calls',
  'dialpad-texts-daily': 'Dialpad texts',
};

function num(value: unknown): number | null {
  if (value === null || value === undefined || value === '') return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

function whyMissing(coverage: Coverage[], slug: string): string {
  const c = coverage.find((x) => x.slug === slug);
  if (!c) return 'Not set up yet.';
  if (!c.enabled) return 'This feed is switched off, so it is not being updated.';
  if (c.rows === 0) return 'Switched on, but it has not completed an update yet.';
  if (c.lastStatus && c.lastStatus !== 'ok' && c.lastStatus !== 'success') {
    return `The last sync did not finish cleanly (${c.lastStatus}).`;
  }
  return 'No data for this period.';
}

/** One headline number, with last week beside it. */
function Tile({
  label,
  value,
  previous,
  help,
  missingReason,
}: {
  label: string;
  value: number | null;
  previous?: number | null;
  help?: string;
  missingReason?: string;
}) {
  const delta = value !== null && previous !== null && previous !== undefined ? value - previous : null;
  return (
    <div className="card" style={{ padding: '14px 16px' }}>
      <div style={{ fontSize: 12, color: 'var(--muted)', fontWeight: 600 }}>{label}</div>
      <div style={{ fontSize: 28, fontWeight: 700, lineHeight: 1.15, margin: '4px 0 2px' }}>
        {value === null ? <span style={{ color: 'var(--muted)' }}>—</span> : value.toLocaleString()}
      </div>
      {value === null ? (
        <div style={{ fontSize: 11.5, color: 'var(--muted)' }}>{missingReason ?? 'Not measured.'}</div>
      ) : delta === null ? (
        <div style={{ fontSize: 11.5, color: 'var(--muted)' }}>No previous week to compare.</div>
      ) : (
        <div style={{ fontSize: 11.5, color: 'var(--muted)' }}>
          {delta === 0 ? 'Level on last week' : `${delta > 0 ? '▲' : '▼'} ${Math.abs(delta).toLocaleString()} on last week`}
        </div>
      )}
      {help ? <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 6 }}>{help}</div> : null}
    </div>
  );
}

export function Dashboard({
  user,
  onOpenReports,
  simple = false,
}: {
  user: User;
  onOpenReports: () => void;
  /** Director view: no refresh controls, and the feed table folded away. */
  simple?: boolean;
}) {
  const [week, setWeek] = useState<string>('');
  const { data, error, loading, reload } = useAsync(
    () => api.get<DashboardData>(`/api/dashboard${week ? `?week=${encodeURIComponent(week)}` : ''}`),
    [week],
  );
  const [openTeam, setOpenTeam] = useState<string | null>(null);

  if (loading && !data) return <Spinner label="Loading the latest figures…" />;
  if (error) return <Banner tone="error" title="Could not load the dashboard. ">{error}</Banner>;
  if (!data) return null;

  const feeds = (
    <section style={{ marginTop: simple ? 12 : 0 }}>
      <h2 style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--lilac-dark)', margin: '0 0 10px' }}>
        Where these figures come from
      </h2>
      <div className="card" style={{ padding: 0, overflowX: 'auto' }}>
        <table className="data-table">
          <thead>
            <tr>
              <th>Feed</th>
              <th>Status</th>
              <th>Last updated</th>
              <th>History held</th>
            </tr>
          </thead>
          <tbody>
            {data.coverage.map((c) => (
              <tr key={c.slug}>
                <td>{DATASET_LABELS[c.slug] ?? c.slug}</td>
                <td>
                  {/*
                    Enabled is checked FIRST and on its own. This used to read
                    "Collecting" for any feed that had rows, so a switched-off
                    feed with old data from a manual run looked live. That is
                    the difference between "these numbers are current" and
                    "these numbers stopped moving a fortnight ago".
                  */}
                  {!c.enabled
                    ? c.rows === 0
                      ? 'Not switched on'
                      : 'Switched OFF — not updating'
                    : c.rows === 0
                      ? 'On, waiting for its first update'
                      : c.lastStatus && c.lastStatus !== 'ok' && c.lastStatus !== 'success'
                        ? `Last update: ${c.lastStatus}`
                        : 'Collecting'}
                </td>
                <td>{c.lastSync ? formatDateTime(c.lastSync) : '—'}</td>
                <td>{c.earliest && c.latest ? `${c.earliest} to ${c.latest}` : '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {user.role !== 'viewer' ? (
        <p style={{ fontSize: 11.5, color: 'var(--muted)', margin: '8px 2px 0' }}>
          Feeds are switched on under Stored data. A feed that is off collects no history, and history
          cannot be recovered later for open vacancies.
        </p>
      ) : null}
    </section>
  );

  const t = data.totals ?? {};
  const p = data.previousTotals ?? {};
  const activityMissing = whyMissing(data.coverage, 'bullhorn-weekly-activity');
  const vacancyMissing = whyMissing(data.coverage, 'bullhorn-open-vacancies');
  // Calls are counted from BULLHORN NOTES (outbound + inbound call actions),
  // not from Dialpad. This used to read the Dialpad feed's status, so a missing
  // calls figure pointed at the wrong integration entirely. Dialpad's own daily
  // call and text datasets feed different reporting.
  const callsMissing = activityMissing;

  if (!data.week) {
    return (
      <>
        <div className="page-head">
          <div>
            <h1>Dashboard</h1>
            <p>Last full week at a glance.</p>
          </div>
        </div>
        <EmptyState title="No complete week has been collected yet">
          {activityMissing} Once the overnight sync has run, this page fills in on its own and grows a
          longer history each week. Nothing needs doing here — reports you run yourself still work.
        </EmptyState>
      </>
    );
  }

  const people = openTeam ? data.people.filter((r) => String(r.team) === openTeam) : [];

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Dashboard</h1>
          <p>
            Week ending <strong>{data.week}</strong>
            {data.previousWeek ? <> · compared with {data.previousWeek}</> : null}
            {data.weeksHeld ? <> · {data.weeksHeld} complete week{data.weeksHeld === 1 ? '' : 's'} held</> : null}
          </p>
          {data.inProgressWeek ? (
            <p style={{ fontSize: 11.5, color: 'var(--muted)', margin: '2px 0 0' }}>
              The week in progress (ending {data.inProgressWeek}) is deliberately left out — a part week
              beside a whole one reads as a collapse.
            </p>
          ) : null}
        </div>
        <div className="page-actions" style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          {(data.availableWeeks ?? []).length > 1 ? (
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12.5 }}>
              <span style={{ color: 'var(--muted)' }}>Week ending</span>
              <select value={data.week ?? ''} onChange={(e) => setWeek(e.target.value)}>
                {data.availableWeeks.map((w, i) => (
                  <option key={w} value={w}>
                    {w}{i === 0 ? ' (latest)' : ''}
                  </option>
                ))}
              </select>
            </label>
          ) : null}
          <button className="btn" onClick={onOpenReports}>
            Run a report
          </button>
        </div>
      </div>

      {/* Share sits ABOVE refresh on purpose. Sending last week's summary is a
          Monday-morning action; refreshing the figures is an occasional one, and
          the panel a director needs most should not be the second one down. */}
      <ShareSummary week={data.week} />

      {/* Refetch, never window.location.reload(): a full page reload remounts
          this component, which polls again, which is how the reload loop
          happened in the first place. */}
      {simple ? null : <SyncAll onFinished={reload} />}

      {(data.coverage ?? []).some((c) => !c.enabled && c.rows > 0) ? (
        <Banner tone="warn" title="Some feeds are switched off. ">
          {(data.coverage.filter((c) => !c.enabled && c.rows > 0).map((c) => DATASET_LABELS[c.slug] ?? c.slug)).join(', ')}
          {' '}still show figures from an earlier manual update, but they are no longer refreshing on their
          own. Anything below that depends on them will drift further out of date each day.
        </Banner>
      ) : null}

      {(data.warnings ?? []).length ? (
        <Banner tone="warn" title="Worth knowing about these figures. ">
          <ul style={{ margin: '6px 0 0 18px', padding: 0 }}>
            {data.warnings.map((w) => (
              <li key={w} style={{ marginBottom: 3 }}>{w}</li>
            ))}
          </ul>
        </Banner>
      ) : null}

      <div className="grid-cards" style={{ marginBottom: 22 }}>
        <Tile label="Placements" value={num(t.placements_added)} previous={num(p.placements_added)} missingReason={activityMissing} help="Recorded in Bullhorn that week" />
        <Tile label="Interviews booked" value={num(t.interviews)} previous={num(p.interviews)} missingReason={activityMissing} />
        <Tile label="Open vacancies" value={data.openVacancies} missingReason={vacancyMissing} help="Right now, not end of week" />
        <Tile label="Ageing and unworked" value={data.agingUnworked} missingReason={vacancyMissing} help="Open past 30 days with no candidates" />
        <Tile label="Calls" value={num(t.calls_total)} previous={num(p.calls_total)} missingReason={callsMissing} help="Logged in Bullhorn, not Dialpad" />
        <Tile label="Outreach replies" value={num(t.outreach_replies)} previous={num(p.outreach_replies)} missingReason={activityMissing} help="Candidates who replied to a sequence" />
      </div>

      <section style={{ marginBottom: 22 }}>
        <h2 style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--lilac-dark)', margin: '0 0 10px' }}>By desk</h2>
        <div className="card" style={{ padding: 0, overflowX: 'auto' }}>
          <table className="data-table">
            <thead>
              <tr>
                <th>Desk</th>
                <th>People</th>
                <th>Placements</th>
                <th>Interviews</th>
                <th>Put forward</th>
                <th>Vacancies worked</th>
                <th>Replies</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {data.teams.map((team) => {
                const name = String(team.team);
                const isOpen = openTeam === name;
                return (
                  <tr key={name}>
                    <td><strong>{name}</strong></td>
                    <td>{num(team.people) ?? '—'}</td>
                    <td>{num(team.placements_added)?.toLocaleString() ?? '—'}</td>
                    <td>{num(team.interviews)?.toLocaleString() ?? '—'}</td>
                    <td>{num(team.submissions_created)?.toLocaleString() ?? '—'}</td>
                    <td>{num(team.jobs_worked)?.toLocaleString() ?? '—'}</td>
                    <td>{num(team.outreach_replies)?.toLocaleString() ?? '—'}</td>
                    <td style={{ textAlign: 'right' }}>
                      <button className="btn btn-quiet" onClick={() => setOpenTeam(isOpen ? null : name)}>
                        {isOpen ? 'Hide people' : 'Show people'}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <p style={{ fontSize: 11.5, color: 'var(--muted)', margin: '8px 2px 0' }}>
          <strong>Put forward</strong> counts candidates added to a vacancy, and a large number is often one
          bulk add rather than a busy week — read it next to <strong>vacancies worked</strong>. CVs sent to
          clients are not tracked; see the notes on any report that uses these figures.
        </p>
      </section>

      {openTeam ? (
        <section style={{ marginBottom: 22 }}>
          <h2 style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--lilac-dark)', margin: '0 0 10px' }}>
            {openTeam} — week ending {data.week}
          </h2>
          <div className="card" style={{ padding: 0, overflowX: 'auto' }}>
            <table className="data-table">
              <thead>
                <tr>
                  <th>Consultant</th>
                  <th>Placements</th>
                  <th>Interviews</th>
                  <th>Put forward</th>
                  <th>Vacancies</th>
                  <th>Calls</th>
                  <th>Replies</th>
                </tr>
              </thead>
              <tbody>
                {people.map((row) => (
                  <tr key={String(row.consultant_id)}>
                    <td>{String(row.consultant ?? '')}</td>
                    <td>{num(row.placements_added)?.toLocaleString() ?? '—'}</td>
                    <td>{num(row.interviews)?.toLocaleString() ?? '—'}</td>
                    <td>{num(row.submissions_created)?.toLocaleString() ?? '—'}</td>
                    <td>{num(row.jobs_worked)?.toLocaleString() ?? '—'}</td>
                    <td>{num(row.calls_total)?.toLocaleString() ?? '—'}</td>
                    <td>{num(row.outreach_replies)?.toLocaleString() ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p style={{ fontSize: 11.5, color: 'var(--muted)', margin: '8px 2px 0' }}>
            A dash means the figure was not collected for that week, which is not the same as none.
          </p>
        </section>
      ) : null}

      {/*
        Shown to everyone, not tucked into an admin page. A director reading a
        low number deserves to know whether the feed behind it ran, and if this
        panel only existed for administrators the question would arrive as
        "these numbers look wrong" three weeks later.
      */}
      {/* Director view keeps the feed table one click away rather than removing
          it: "did the feed behind this number run?" still deserves an answer. */}
      {simple ? (
        <details className="about-figures">
          <summary>Where these figures come from</summary>
          {feeds}
        </details>
      ) : (
        feeds
      )}
    </>
  );
}
